import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Spinner,
} from "@opero/ui";
import { OPERO_LOGOS } from "@opero/ui/brand";
import { getErrorMessage } from "@digiits-agency/polaris-sdk";
import {
  discoverLoginMethod,
  redirectToIdp,
  startSsoByEmail,
} from "../lib/sso";
import { AuthModalShell } from "./AuthModalShell";

type Status = "idle" | "submitting" | "redirecting";

interface SsoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

export function SsoModal({ isOpen, onClose, onSwitchToLogin }: SsoModalProps) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setEmail(searchParams.get("email") ?? "");
    setStatus("idle");
    setError(null);
  }, [isOpen, searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter your work email to continue.");
      return;
    }
    setError(null);
    setStatus("submitting");

    try {
      const discovered = await discoverLoginMethod(trimmed);
      if (discovered.mode === "password") {
        throw new Error(
          "That address doesn't use SSO. Sign in with your email and password instead.",
        );
      }

      setStatus("redirecting");
      // `initiateUrl` is the IdP URL discovery already resolved — following it
      // saves a second round trip. `startSsoByEmail` is the fallback for a
      // response that names a mode but no URL.
      if (discovered.initiateUrl) {
        redirectToIdp(discovered.initiateUrl);
        return;
      }
      await startSsoByEmail(trimmed);
    } catch (err: unknown) {
      setStatus("idle");
      setError(
        getErrorMessage(
          err,
          "We couldn't find an SSO provider for that email. Check the address or use email sign in.",
        ),
      );
    }
  };

  const goToLogin = () => {
    onClose();
    onSwitchToLogin();
  };

  return (
    <AuthModalShell
      isOpen={isOpen}
      onClose={onClose}
      brand={<img src={OPERO_LOGOS.logoFull} alt="Opero" className="h-7" />}
    >
      <div className="px-7 pb-7 pt-2">
        <div className="mb-5 text-center">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Sign in with SSO
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Enter your work email to continue with your
            organization&apos;s identity provider.
          </p>
        </div>

        {status === "redirecting" ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Spinner size="lg" />
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Redirecting to your provider…
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="text-sm text-danger">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="sso-email">Work email</Label>
              <Input
                id="sso-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "submitting"}
                autoFocus
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? (
                <>
                  <Spinner size="sm" /> Finding your provider…
                </>
              ) : (
                "Continue"
              )}
            </Button>

            <p className="pt-2 text-center text-sm text-slate-500 dark:text-slate-400">
              <button
                type="button"
                onClick={goToLogin}
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                ← Back to email sign in
              </button>
            </p>
          </form>
        )}
      </div>
    </AuthModalShell>
  );
}
